import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { artStyles, searchArtStyles } from '../data/artStyles';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../config/backendConfig';
import StepByStepLearning from '../components/StepByStepLearning';
import ArtformDetailAccordions from '../components/ArtformDetailAccordions';
import KidBackground from '../ui/backgrounds/KidBackground';
import splashImage from '../assets/splash.png';
import { saveWaysOfSeeingJourney, addTransformationStep, checkExistingActivity, getOrCreateArtStyleUnlockState, addUnlockedArtStyles } from '../utils/activityService';
import { resolveImageUrl } from '../utils/urlResolver';
import { enqueueAndPoll } from '../utils/jobPolling';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from '../components/UpgradeQuotaModal';

/**
 * Local Node server mounts reimagine at `/api/reimagine`.
 * Firebase HTTPS function base is `...cloudfunctions.net/api` (function name `api`); Express also mounts `/reimagine` for that host.
 */
function getReimagineApiRoot() {
  const base = String(API_ENDPOINTS.backendBase || '').replace(/\/$/, '');
  if (/\.cloudfunctions\.net\/api$/i.test(base)) {
    return `${base}/reimagine`;
  }
  return `${base}/api/reimagine`;
}

// Reusable Unlock Styles Modal Component
const UnlockStylesModal = ({ isOpen, onClose, slotsToUnlock, availableStyles, artworkUrl, userId, title, description, onUnlockComplete }) => {
  const [selectedUnlockIds, setSelectedUnlockIds] = useState([]);

  useEffect(() => {
    if (isOpen && slotsToUnlock > 0 && availableStyles.length > 0) {
      // Pre-select first N styles
      setSelectedUnlockIds(availableStyles.slice(0, slotsToUnlock).map(s => s.id));
    }
  }, [isOpen, slotsToUnlock, availableStyles]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100000005] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white border-2 border-amber-300 rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[80vh] overflow-hidden flex flex-col">
        <h3 className="text-2xl font-bold text-slate-900 mb-3 text-center">
          {title}
        </h3>
        <p className="text-slate-700 mb-4 text-base text-center leading-relaxed">
          {description}
        </p>
        <div className="space-y-3 flex-1 overflow-y-auto pr-2">
          {availableStyles.map((style) => {
            const checked = selectedUnlockIds.includes(style.id);
            const disabled = !checked && selectedUnlockIds.length >= slotsToUnlock;
            return (
              <label
                key={style.id}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all ${
                  checked
                    ? 'border-amber-500 bg-amber-100 shadow-md'
                    : 'border-amber-200 bg-white hover:border-amber-300 hover:bg-amber-50'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 text-amber-600 rounded border-2 border-amber-400 focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => {
                    setSelectedUnlockIds((prev) => {
                      if (prev.includes(style.id)) {
                        return prev.filter((id) => id !== style.id);
                      }
                      if (prev.length >= slotsToUnlock) {
                        return prev;
                      }
                      return [...prev, style.id];
                    });
                  }}
                />
                <div className="flex-1">
                  <div className="font-bold text-lg text-slate-900 mb-1">
                    {style.name}
                  </div>
                  <div className="text-sm text-slate-700 leading-relaxed">
                    {style.briefDescription ||
                      style.kidFriendlyDescription ||
                      'Transform your artwork with this unique style.'}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="flex gap-3 mt-5 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-full border-2 border-amber-300 text-slate-700 bg-white hover:bg-amber-50 font-semibold text-base transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              if (selectedUnlockIds.length > 0 && onUnlockComplete) {
                await onUnlockComplete(selectedUnlockIds);
              }
            }}
            className="flex-1 px-4 py-3 rounded-full text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            disabled={selectedUnlockIds.length === 0}
          >
            Unlock Selected ({selectedUnlockIds.length}/{slotsToUnlock})
          </button>
        </div>
      </div>
    </div>
  );
};

const ArtStyleSelectionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkQuotaBeforeUse, recordUsage, showUpgradeModal, dismissUpgradeModal, upgradeModalFeature } = useFeatureQuota();
  const { artwork: artworkFromState, journeyId, continueJourney, preSelectedStyle, forceGenerate, preOpenUpload } = location.state || {};
  
  // Try to get artwork from location.state first, then fallback to localStorage (for mentor uploads)
  const artwork = useMemo(() => {
    if (artworkFromState) {
      console.log('✅ ArtStyleSelectionPage: Artwork found in location.state');
      return artworkFromState;
    }
    
    // Fallback: Check localStorage for mentor-selected artwork
    try {
      const storedArtwork = localStorage.getItem('mentorSelectedArtwork');
      if (storedArtwork) {
        const parsed = JSON.parse(storedArtwork);
        console.log('✅ ArtStyleSelectionPage: Artwork found in localStorage:', parsed);
        // Convert localStorage format to match location.state format
        return {
          imageUrl: parsed.file,
          originalArtwork: parsed.file,
          url: parsed.file,
          artworkUrl: parsed.file,
          title: parsed.name,
          artist: parsed.artist
        };
      }
    } catch (e) {
      console.warn('⚠️ ArtStyleSelectionPage: Failed to parse localStorage artwork:', e);
    }
    
    console.log('⚠️ ArtStyleSelectionPage: No artwork found in location.state or localStorage');
    return null;
  }, [artworkFromState]);
  
  console.log('🎨 ArtStyleSelectionPage: Final artwork object:', artwork);
  console.log('🎨 ArtStyleSelectionPage: artwork type:', typeof artwork);
  console.log('🎨 ArtStyleSelectionPage: artwork keys:', artwork ? Object.keys(artwork) : 'null');
  console.log('🎨 ArtStyleSelectionPage: Full location.state:', location.state);
  
  // Extract artwork URL for display and processing - use useMemo to recompute when artwork changes
  const artworkUrlForDisplay = useMemo(() => {
    console.log('🔍 ArtStyleSelectionPage: useMemo triggered, artwork:', artwork);
    console.log('🔍 ArtStyleSelectionPage: artwork type:', typeof artwork);
    console.log('🔍 ArtStyleSelectionPage: artwork keys:', artwork ? Object.keys(artwork) : 'null');
    
    if (!artwork) {
      console.log('⚠️ ArtStyleSelectionPage: No artwork provided');
      return null;
    }
    
    // Helper function to resolve localhost URLs
    const resolveLocalhostUrl = (url) => {
      if (!url || typeof url !== 'string') return url;
      if (url.includes('localhost:3001')) {
        // Get backend base URL with fallback
        const backendBase = API_ENDPOINTS?.backendBase || 
                           (typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
                             ? 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
                             : 'http://localhost:3001');
        const urlPath = url.replace(/^https?:\/\/[^/]+/, '');
        const resolved = `${backendBase}${urlPath}`;
        console.log('🔗 ArtStyleSelectionPage: Resolved localhost URL:', url.substring(0, 100), '->', resolved.substring(0, 100) + '...');
        return resolved;
      }
      return url;
    };
    
    // If artwork is a string, use it directly (but resolve localhost URLs)
    if (typeof artwork === 'string') {
      const url = resolveLocalhostUrl(artwork);
      console.log('✅ ArtStyleSelectionPage: Artwork is a string URL:', url.substring(0, 100));
      return url;
    }
    
    // Try multiple possible properties for artwork URL
    // For tutorial uploads, artworkUrl is used in sharedArtworks
    let url = artwork?.imageUrl || artwork?.artworkUrl || artwork?.originalArtwork || artwork?.url || null;
    
    // Resolve localhost URLs and relative paths to production backend URL
    url = resolveLocalhostUrl(url);
    
    // Use centralized URL resolver for additional handling (relative paths, etc.)
    if (url) {
      url = resolveImageUrl(url);
    }
    
    if (url) {
      console.log('✅ ArtStyleSelectionPage: Extracted artwork URL from object:', url.substring(0, 100));
      console.log('✅ ArtStyleSelectionPage: URL found in property:', 
        artwork?.imageUrl ? 'imageUrl' :
        artwork?.originalArtwork ? 'originalArtwork' :
        artwork?.url ? 'url' :
        artwork?.artworkUrl ? 'artworkUrl' : 'unknown'
      );
      console.log('✅ ArtStyleSelectionPage: Full URL:', url);
    } else {
      console.error('❌ ArtStyleSelectionPage: Could not extract artwork URL from object');
      console.error('❌ ArtStyleSelectionPage: artwork object:', JSON.stringify(artwork, null, 2));
      console.error('❌ ArtStyleSelectionPage: artwork.imageUrl:', artwork?.imageUrl);
      console.error('❌ ArtStyleSelectionPage: artwork.originalArtwork:', artwork?.originalArtwork);
      console.error('❌ ArtStyleSelectionPage: artwork.url:', artwork?.url);
      console.error('❌ ArtStyleSelectionPage: artwork.artworkUrl:', artwork?.artworkUrl);
    }
    
    return url;
  }, [artwork]);
  
  // Log the resolved URL with detailed info
  console.log('🎨 ArtStyleSelectionPage: Final artworkUrlForDisplay:', artworkUrlForDisplay ? artworkUrlForDisplay.substring(0, 100) : 'null');
  console.log('🎨 ArtStyleSelectionPage: API_ENDPOINTS.backendBase:', API_ENDPOINTS?.backendBase || 'NOT SET');
  console.log('🎨 ArtStyleSelectionPage: window.location.hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
  if (artworkUrlForDisplay && artworkUrlForDisplay.includes('localhost')) {
    console.error('❌ ERROR: artworkUrlForDisplay still contains localhost! This should have been resolved.');
    console.error('❌ Original artwork:', artwork);
  }
  
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [transformedArtwork, setTransformedArtwork] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStyles, setFilteredStyles] = useState(artStyles);
  const [showResults, setShowResults] = useState(false);
  const [showExpandedView, setShowExpandedView] = useState(false);
  const [showStepByStepLearning, setShowStepByStepLearning] = useState(false);
  const [showArtworkFullscreen, setShowArtworkFullscreen] = useState(false);
  // const [forceRender, setForceRender] = useState(0); // Force re-render counter - removed unused variable
  const [currentJourneyId, setCurrentJourneyId] = useState(journeyId || null); // Track current reimagine journey
  const [userPrompt, setUserPrompt] = useState(''); // Track user's prompt
  const [showConfirmation, setShowConfirmation] = useState(false); // Track confirmation popup
  const [unlockedStyleIds, setUnlockedStyleIds] = useState(null); // Ways of Seeing unlock state for this artwork
  const [unlockStateLoading, setUnlockStateLoading] = useState(false);
  const [pendingUnlockSlots, setPendingUnlockSlots] = useState(0); // Pending unlock slots
  const [showFirst3Modal, setShowFirst3Modal] = useState(false); // Modal for choosing first 3 styles
  const [showPendingUnlockModal, setShowPendingUnlockModal] = useState(false); // Modal for claiming pending slots

  // Check user age for processing time
  const userAge = user?.userAge || 18;
  const isUnder13 = userAge < 13;

  /** Stable payload for shared artform + Ways of Seeing fallbacks (expanded modal accordions). */
  const waysOfSeeingAccordionMeta = useMemo(() => {
    if (!selectedStyle) return null;
    return {
      name: selectedStyle.name,
      description: selectedStyle.description,
      kidFriendlyDescription: selectedStyle.kidFriendlyDescription,
      characteristics: selectedStyle.characteristics || [],
      famousArtists: selectedStyle.famousArtists || [],
      yearRange: selectedStyle.yearRange,
    };
  }, [selectedStyle]);

  // Debug useEffect to monitor isProcessing state
  useEffect(() => {
    console.log('🎨 isProcessing state changed:', isProcessing);
  }, [isProcessing]);

  // Debug useEffect to monitor artwork state changes
  useEffect(() => {
    console.log('🎨 ArtStyleSelectionPage: artwork state changed');
    console.log('🎨 ArtStyleSelectionPage: artwork:', artwork);
    console.log('🎨 ArtStyleSelectionPage: artworkUrlForDisplay:', artworkUrlForDisplay);
    console.log('🎨 ArtStyleSelectionPage: location.state:', location.state);
  }, [artwork, artworkUrlForDisplay, location.state]);

  // Convert local URLs to full backend URLs
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return imageUrl;
    
    // Data/blobs are already resolvable by the browser
    if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
      return imageUrl;
    }
    
    // Replace localhost URLs with production backend URL
    if (imageUrl.includes('localhost:3001')) {
      // Get backend base URL with fallback
      const backendBase = API_ENDPOINTS?.backendBase || 
                         (typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
                           ? 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
                           : 'http://localhost:3001');
      // Extract the path from localhost URL and prepend backend base
      const urlPath = imageUrl.replace(/^https?:\/\/[^/]+/, '');
      const resolvedUrl = `${backendBase}${urlPath}`;
      console.log('🔗 Resolved localhost URL:', imageUrl, '->', resolvedUrl);
      return resolvedUrl;
    }
    
    // Full URLs (but not localhost)
    if (/^https?:\/\//i.test(imageUrl)) {
      return imageUrl;
    }
    
    // Absolute uploads path
    if (imageUrl.startsWith('/uploads')) {
      return `${API_ENDPOINTS.backendBase}${imageUrl}`;
    }
    
    // Relative uploads path (missing leading slash)
    if (imageUrl.startsWith('uploads')) {
      return `${API_ENDPOINTS.backendBase}/${imageUrl}`;
    }
    
    // Bare filename (e.g., abc.jpg) -> assume uploads
    if (!imageUrl.startsWith('/')) {
      return `${API_ENDPOINTS.backendBase}/uploads/${imageUrl}`;
    }
    
    return imageUrl;
  };

  // Load or create art style unlock state for this artwork and user
  useEffect(() => {
    const loadUnlockState = async () => {
      if (!artworkUrlForDisplay || !user?.uid) {
        return;
      }
      try {
        setUnlockStateLoading(true);
        const state = await getOrCreateArtStyleUnlockState(artworkUrlForDisplay, user.uid);
        const unlocked = state.unlockedStyleIds || [];
        const pending = state.pendingUnlockSlots || 0;
        setUnlockedStyleIds(unlocked);
        setPendingUnlockSlots(pending);
        
        // If no styles unlocked yet AND no pending slots, show "Choose your first 3 styles" modal
        // If there are pending slots, the badge will be visible to claim them first
        if (unlocked.length === 0 && pending === 0 && !showFirst3Modal) {
          setShowFirst3Modal(true);
        }
      } catch (error) {
        console.error('❌ Failed to load Ways of Seeing unlock state:', error);
        setUnlockedStyleIds(null);
        setPendingUnlockSlots(0);
      } finally {
        setUnlockStateLoading(false);
      }
    };

    loadUnlockState();
  }, [artworkUrlForDisplay, user?.uid]);

  useEffect(() => {
    if (searchQuery) {
      setFilteredStyles(searchArtStyles(searchQuery));
    } else {
      setFilteredStyles(artStyles);
    }
  }, [searchQuery]);

  // Auto-select pre-selected style if provided
  useEffect(() => {
    console.log('🎨 Style selection effect triggered:', {
      preSelectedStyle: preSelectedStyle?.name,
      selectedStyle: selectedStyle?.name,
      forceGenerate
    });
    
    if (preSelectedStyle && !selectedStyle) {
      console.log('🎨 Auto-selecting pre-selected style:', preSelectedStyle);
      setSelectedStyle(preSelectedStyle);
      
      // If forceGenerate is true, automatically start tutorial generation
      if (forceGenerate) {
        console.log('🎨 Force generate flag detected, starting tutorial generation...');
        setTimeout(() => {
          console.log('🎨 Starting tutorial generation with style:', preSelectedStyle.name);
          setShowStepByStepLearning(true);
        }, 1000); // Small delay to ensure state is set
      }
    }
  }, [preSelectedStyle, selectedStyle, forceGenerate]);

  // If user arrived intending to upload a new artwork, open Artist Wall upload in-place
  useEffect(() => {
    if (preOpenUpload && !artwork) {
      // Open Artist Wall upload modal on Artist Corner page itself
      navigate('/artist-wall', {
        state: { openUpload: true, redirectTo: '/art-world/selection' },
      });
    }
  }, [preOpenUpload, artwork, navigate]);

  const handleStyleSelect = (style) => {
    console.log('🎨 Style selected:', style);
    console.log('Style ID:', style.id);
    console.log('Style Name:', style.name);
    setSelectedStyle(style);
    setShowConfirmation(true);
    setShowResults(false);
    setTransformedArtwork(null);
  };

  const handleStartTutorial = async () => {
    if (!selectedStyle) {
      alert('Please select an art style first!');
      return;
    }

    // Use artworkUrlForDisplay for consistency; if it's missing, fall back to the
    // transformedArtwork URL. This ensures the button still works even if the
    // original URL was not propagated correctly into this page.
    const artworkUrl = (artworkUrlForDisplay || transformedArtwork || '').trim();
    
    console.log('🎨 handleStartTutorial called');
    console.log('🎨 artworkUrlForDisplay:', artworkUrlForDisplay ? artworkUrlForDisplay.substring(0, 100) : 'none');
    console.log('🎨 selectedStyle:', selectedStyle?.name);
    console.log('🎨 artwork object:', artwork);
    
    if (!artworkUrl) {
      console.error('❌ No artwork URL available for tutorial generation');
      alert('Error: Artwork URL is missing. Please select an artwork first.');
      return;
    }
    
    // Check if tutorial already exists for this artwork and style
    console.log('🔍 Checking for existing tutorial activity:', {
      artworkUrl: artworkUrl.substring(0, 100),
      styleId: selectedStyle?.id,
      styleName: selectedStyle?.name,
      userType: user?.accountType || 'student'
    });
    
    const existingActivity = await checkExistingActivity(artworkUrl, 'tutorial', selectedStyle);
    
    if (existingActivity) {
      console.log('✅ Found existing tutorial activity:', {
        activityId: existingActivity.id,
        userType: user?.accountType || 'student'
      });
    } else {
      console.log('ℹ️ No existing tutorial found, will create new one');
    }

    // Regardless of whether an existing tutorial is found, open the Step-by-Step flow here
    // (The tutorial component itself can decide to reuse cached content or generate fresh.)
    setShowStepByStepLearning(true);
  };

  // Check for existing style transfer cache
  const checkStyleTransferCache = async (imageUrl, styleId, prompt) => {
    try {
      const params = new URLSearchParams({
        imageUrl: String(imageUrl ?? ''),
        styleId: String(styleId ?? ''),
        prompt: String(prompt ?? ''),
        size: '1024x1024'
      });
      
      const checkUrl = `${getReimagineApiRoot()}/style-transfer/check?${params}`;
      const response = await fetch(checkUrl);
      const text = await response.text();
      let data = { cached: false };
      try {
        data = text ? JSON.parse(text) : { cached: false };
      } catch {
        console.warn('⚠️ Style-transfer cache check: non-JSON response', response.status);
        return { cached: false };
      }
      
      console.log('🔍 Cache check result:', data);
      return data;
    } catch (error) {
      console.error('❌ Cache check failed:', error);
      return { cached: false };
    }
  };

  // Fetch style metadata from Firebase
  const fetchStyleMetadata = async (styleId) => {
    try {
      const response = await fetch(`${getReimagineApiRoot()}/style-meta/${styleId}`);
      const data = await response.json();
      
      console.log('📊 Style metadata fetched:', data);
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch style metadata:', error);
      return null;
    }
  };

  const handleTransformArtwork = async () => {
    console.log('🎨 handleTransformArtwork called');
    console.log('🎨 Selected style:', selectedStyle);
    console.log('🎨 Artwork URL:', artwork);
    console.log('🎨 User prompt:', userPrompt);
    console.log('🎨 Current journey ID:', currentJourneyId);
    console.log('🎨 User:', user);
    
    if (!selectedStyle) {
      alert('Please select an art style first!');
      return;
    }

    if (!artwork) {
      alert('No artwork found! Please go back to AI Magic and upload an artwork first.');
      return;
    }

    console.log('Current user state:', user);
    console.log('Current user UID:', user?.uid);
    console.log('Artwork URL being used:', artwork);

    if (!user?.uid) {
      alert('Please log in to transform your artwork!');
      return;
    }

    const quotaCheck = await checkQuotaBeforeUse('styleTransfer');
    if (!quotaCheck.allowed) {
      if (quotaCheck.overLimit) return;
      return;
    }

    console.log('🎨 Starting transformation process...');
    console.log('Setting isProcessing to true');
    setIsProcessing(true);

    console.log('isProcessing state after setting:', true);
    
    // Track start time for the entire process
    const processStartTime = Date.now();
    const minProcessTime = 5000; // Minimum 5 seconds for the entire process
    
    try {
      // Use artworkUrlForDisplay which is already resolved, or extract from artwork object
      let artworkUrl = artworkUrlForDisplay;
      
      // If artworkUrlForDisplay is not available, extract from artwork object
      if (!artworkUrl) {
        console.log('🎨 Artwork object:', artwork);
        console.log('🎨 Artwork type:', typeof artwork);
        console.log('🎨 Artwork.imageUrl:', artwork?.imageUrl);
        console.log('🎨 Artwork.url:', artwork?.url);
        console.log('🎨 Artwork.originalArtwork:', artwork?.originalArtwork);
        console.log('🎨 Artwork.artworkUrl:', artwork?.artworkUrl);
        
        // Try multiple possible properties for artwork URL
        // For tutorial uploads, artworkUrl is used in sharedArtworks, so check it first
        artworkUrl = typeof artwork === 'string' 
          ? artwork 
          : artwork?.imageUrl || artwork?.artworkUrl || artwork?.originalArtwork || artwork?.url || artwork;
        
        // If artworkUrl is still an object, try to extract from it
        if (artworkUrl && typeof artworkUrl === 'object') {
          artworkUrl = artworkUrl?.imageUrl || artworkUrl?.artworkUrl || artworkUrl?.url || artworkUrl?.originalArtwork || null;
        }
      }
      
      console.log('🎨 Using artworkUrl:', artworkUrl ? artworkUrl.substring(0, 100) + '...' : 'null');
      console.log('🎨 artworkUrl type:', typeof artworkUrl);
      console.log('🎨 artworkUrl length:', artworkUrl ? artworkUrl.length : 0);
      
      // Resolve localhost URLs and relative paths to production backend URL (safety check)
      if (artworkUrl && typeof artworkUrl === 'string') {
        // Use centralized URL resolver for consistent handling
        const resolvedUrl = resolveImageUrl(artworkUrl);
        if (resolvedUrl !== artworkUrl) {
          console.log('🔗 Resolved artwork URL:', artworkUrl.substring(0, 100) + '...', '->', resolvedUrl.substring(0, 100) + '...');
          artworkUrl = resolvedUrl;
        }
      }
      
      if (!artworkUrl || typeof artworkUrl !== 'string') {
        console.error('❌ Invalid artwork URL:', artwork);
        console.error('❌ artworkUrl value:', artworkUrl);
        alert('Invalid artwork data. Please try again. Make sure you have selected an artwork.');
        setIsProcessing(false);
        return;
      }
      
      if (artworkUrl.includes('firebasestorage.googleapis.com')) {
        console.log('Converting Firebase URL to base64...');
        try {
          // For Firebase URLs, we'll use the URL directly instead of converting to base64
          // This prevents the document size issue in Firestore
          console.log('Using Firebase URL directly for transformation');
        } catch (conversionError) {
          console.error('Error processing Firebase URL:', conversionError);
          alert('Unable to process the artwork. Please try again.');
          return;
        }
      }

      // Check for existing cache and fetch style metadata
      console.log('🔍 Checking cache for style transfer:', {
        artworkUrl: artworkUrl.substring(0, 100),
        styleId: selectedStyle.id,
        styleName: selectedStyle.name,
        userType: user?.accountType || 'student'
      });
      
      const [cacheResult, styleMetadata] = await Promise.all([
        checkStyleTransferCache(artworkUrl, selectedStyle.id, `Transform this artwork into ${selectedStyle.name} style`),
        fetchStyleMetadata(selectedStyle.id)
      ]);

      if (cacheResult.cached) {
        console.log('✅ Found cached transformation!', {
          cacheKey: cacheResult.cacheKey,
          userType: user?.accountType || 'student',
          cachedImageUrl: cacheResult.generatedImageUrl?.substring(0, 100)
        });
        
        // Use cached result (no quota consumed)
        const transformedImageUrl = cacheResult.generatedImageUrl;
        setTransformedArtwork(transformedImageUrl);
        setShowResults(true);

        // Same as fresh transform: persist Ways of Seeing journey so Magic Studio dashboard shows this activity.
        try {
          if (currentJourneyId) {
            await addTransformationStep(currentJourneyId, {
              prompt: userPrompt || `Transform to ${selectedStyle.name} style`,
              transformedImageUrl,
              feedback: null,
              styleInfo: selectedStyle
            });
          } else {
            const journeyId = await saveWaysOfSeeingJourney({
              originalImageUrl: artworkUrl,
              transformedImageUrl,
              styleInfo: selectedStyle,
              prompt: userPrompt || `Transform to ${selectedStyle.name} style`,
              title: `${selectedStyle.name} Reimagine Journey`,
              description: `Artwork transformation journey with ${selectedStyle.name} style`,
              originalArtworkId: artworkUrl
            }, true);
            if (journeyId) {
              setCurrentJourneyId(journeyId);
            }
          }
        } catch (activityError) {
          console.error('❌ Failed to save ways-of-seeing journey (cached transform):', activityError);
        }

        setIsProcessing(false);
        
        // Add minimum loading time for cached responses
        const elapsedTime = Date.now() - processStartTime;
        const remainingTime = Math.max(0, minProcessTime - elapsedTime);
        if (remainingTime > 0) {
          console.log(`🎯 Cached response detected. Adding ${remainingTime}ms to ensure minimum loading time...`);
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        return;
      }

      await recordUsage('styleTransfer', selectedStyle?.id, artworkUrlForDisplay || artworkUrl);

      // Final check: ensure artworkUrl doesn't contain localhost
      if (artworkUrl && typeof artworkUrl === 'string' && artworkUrl.includes('localhost:3001')) {
        console.error('❌ CRITICAL ERROR: artworkUrl still contains localhost before API call!');
        console.error('❌ artworkUrl:', artworkUrl);
        console.error('❌ API_ENDPOINTS.backendBase:', API_ENDPOINTS?.backendBase || 'NOT SET');
        console.error('❌ window.location.hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
        // Force resolution one more time
        const backendBase = API_ENDPOINTS?.backendBase || 
                           (typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
                             ? 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
                             : 'http://localhost:3001');
        const urlPath = artworkUrl.replace(/^https?:\/\/[^/]+/, '');
        artworkUrl = `${backendBase}${urlPath}`;
        console.log('🔗 FORCED resolution to:', artworkUrl.substring(0, 100) + '...');
      }
      
      console.log('📤 Sending transformation request:', {
        artworkUrl: artworkUrl.substring(0, 100) + '...', // Log first 100 chars
        styleId: selectedStyle.id,
        styleName: selectedStyle.name,
        userId: user?.uid,
        containsLocalhost: artworkUrl.includes('localhost') ? 'YES - ERROR!' : 'NO - OK',
        apiEndpoint: `${getReimagineApiRoot()}/style-transfer`
      });

      const requestBody = {
        imageUrl: artworkUrl,
        styleId: selectedStyle.id,
        prompt: `Transform this artwork into ${selectedStyle.name} style`,
        size: '1024x1024'
      };
      
      console.log('📤 Request body prepared:', {
        imageUrl: requestBody.imageUrl.substring(0, 100) + '...',
        styleId: requestBody.styleId,
        prompt: requestBody.prompt,
        size: requestBody.size
      });
      console.log('📤 Full selectedStyle object:', selectedStyle);

      console.log('🎯 Using background job processing for style-transfer (like reimagine)');
      
      // Use enqueueAndPoll utility (same approach as ReimagineStudio) for background job processing
      // This prevents 408 timeouts and reduces costs by using background workers
      console.log('🚀 Using enqueueAndPoll for style-transfer background processing');
      
      const data = await enqueueAndPoll(
        `${getReimagineApiRoot()}/style-transfer`,
        requestBody,
        {
          interval: 2000, // Poll every 2 seconds
          maxAttempts: 150 // Max 5 minutes (150 * 2s)
        }
      );
      
      console.log('✅ Background job completed:', data);
      
      // enqueueAndPoll returns the result from the worker
      // Worker returns: { success: true, generatedImageUrl, cached, cacheKey, model }
      let generatedImageUrl = data.generatedImageUrl || data.imageUrl || data.result?.generatedImageUrl;
      
      if (!generatedImageUrl) {
        throw new Error('No image URL returned from style transfer');
      }
      
      console.log('Transformed artwork URL:', generatedImageUrl);
      console.log('Is cached:', data.cached);
      
      // Check if this was a cached response
      if (data.cached) {
        console.log('🎯 Cached response detected from backend. Adding minimum loading time...');
        // Add a minimum loading time for cached responses
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // GUARANTEED MINIMUM LOADING TIME - This will ensure the overlay stays visible
      const totalProcessTime = Date.now() - processStartTime;
      const remainingTime = Math.max(0, minProcessTime - totalProcessTime);
      
      if (remainingTime > 0) {
        console.log(`🎨 Process completed quickly (${totalProcessTime}ms). Adding ${remainingTime}ms to ensure minimum loading time...`);
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      if (data.isQueued) {
        // For users under 13, show queued message
        alert(data.message);
        setShowResults(false);
        setIsProcessing(false);
      } else {
        // For users 13+, show transformed artwork
        console.log('Setting transformed artwork:', generatedImageUrl);
        setTransformedArtwork(generatedImageUrl);
        console.log('Setting show results to true');
        setShowResults(true);
        console.log('State after setting:', { transformedArtwork: generatedImageUrl, showResults: true });
        
        // Handle reimagine journey - auto-save or add step
        console.log('🎨 Starting journey save process...');
        console.log('🎨 Current journey ID:', currentJourneyId);
        console.log('🎨 Selected style:', selectedStyle);
        console.log('🎨 User prompt:', userPrompt);
        console.log('🎨 Full response data:', data);
        console.log('🎨 Transformed image URL:', generatedImageUrl);
        
        try {
          if (currentJourneyId) {
            // Add step to existing journey
            console.log('🎨 Adding step to existing journey:', currentJourneyId);
            await addTransformationStep(currentJourneyId, {
              prompt: userPrompt || `Transform to ${selectedStyle.name} style`,
              transformedImageUrl: generatedImageUrl,
              feedback: null, // Will be added later if available
              styleInfo: selectedStyle
            });
            console.log('✅ Transformation step added to journey:', currentJourneyId);
          } else {
            // Create new journey
            console.log('🎨 Creating new journey...');
            console.log('🎨 Journey parameters:', {
              originalImageUrl: artworkUrl,
              transformedImageUrl: data.generatedImageUrl,
              styleInfo: selectedStyle,
              prompt: userPrompt || `Transform to ${selectedStyle.name} style`,
              title: `${selectedStyle.name} Reimagine Journey`,
              description: `Artwork transformation journey with ${selectedStyle.name} style`,
              originalArtworkId: artworkUrl
            });
            
            const journeyId = await saveWaysOfSeeingJourney({
              originalImageUrl: artworkUrl,
              transformedImageUrl: data.generatedImageUrl,
              styleInfo: selectedStyle,
              prompt: userPrompt || `Transform to ${selectedStyle.name} style`,
              title: `${selectedStyle.name} Reimagine Journey`,
              description: `Artwork transformation journey with ${selectedStyle.name} style`,
              originalArtworkId: artworkUrl
            }, true); // Auto-save on initial prompt
            
            setCurrentJourneyId(journeyId);
            console.log('✅ New reimagine journey created:', journeyId);
          }
        } catch (activityError) {
          console.error('❌ Failed to handle reimagine journey:', activityError);
          console.error('❌ Error details:', {
            message: activityError.message,
            stack: activityError.stack
          });
        }
        
        // Ensure the transformed image is loaded before hiding the overlay
        if (data.generatedImageUrl) {
          const img = new Image();
          img.onload = () => {
            console.log('🎨 Transformed image loaded successfully, hiding loading overlay');
            setIsProcessing(false);
          };
          img.onerror = () => {
            console.log('🎨 Transformed image failed to load, but hiding loading overlay anyway');
            setIsProcessing(false);
          };
          img.src = getFullImageUrl(generatedImageUrl);
        } else {
          // No image to load, hide overlay immediately
          setIsProcessing(false);
        }
      }
    } catch (error) {
      console.error('Transformation error:', error);
      
      // Ensure minimum loading time even for errors
      const totalProcessTime = Date.now() - processStartTime;
      const remainingTime = Math.max(0, minProcessTime - totalProcessTime);
      
      if (remainingTime > 0) {
        console.log(`🎨 Error occurred quickly (${totalProcessTime}ms). Adding ${remainingTime}ms to ensure minimum loading time...`);
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Handle different types of errors
      if (error.name === 'SyntaxError') {
        alert('Server returned an invalid response. Please try again.');
      } else if (error.message.includes('Failed to fetch')) {
        alert('Unable to connect to the server. Please check your connection and try again.');
      } else if (error.response?.status === 504) {
        alert('Request timed out. Please try again.');
      } else if (error.response?.status === 400) {
        alert('Invalid request. Please check your selection and try again.');
      } else {
        alert('An error occurred during transformation. Please try again.');
      }
      
      setShowResults(false);
      // Hide loading overlay for errors
      setIsProcessing(false);
    }
  };

  const getProcessingMessage = () => {
    if (isUnder13) {
      return "Our art experts are working on your transformation! This will take about 15 minutes. Check your notifications when it's ready!";
    } else {
      return "Transforming your artwork into beautiful art style... Please wait while AI processes your image!";
    }
  };

  return (
    <div className="artm-page artm-page-pad min-h-screen flex flex-col bg-[#FFF8E1] px-0 py-0 md:px-[2.5cm] md:py-6">
      {/* Amber color splashes background - behind window */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img src={splashImage} alt="" className="absolute top-10 right-5 opacity-20" style={{ width: '400px', height: '200px', objectFit: 'cover', transform: 'rotate(15deg)', mixBlendMode: 'multiply', filter: 'blur(2px)' }} />
        <img src={splashImage} alt="" className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15" style={{ width: '300px', height: '150px', objectFit: 'cover', transform: 'translate(-50%, -50%) rotate(10deg)', mixBlendMode: 'multiply', filter: 'blur(3px)' }} />
      </div>
      <KidBackground variant="blobs" intensity={1} />

      <div className="relative z-10 w-full max-w-full flex-1 min-h-0 flex flex-col py-0 md:py-4">
        <div className="bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 flex flex-col flex-1 min-h-0 overflow-hidden">
          <header className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
            <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px] px-3 md:px-6">
              <p className="text-sm md:text-base font-semibold text-gray-800 truncate pr-2">Choose Your Art Style</p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button type="button" onClick={() => navigate('/my-art-world')} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all" title="Artist Corner">Artist corner</button>
                <button type="button" title={user?.accountType === 'mentor' ? "Open Mentor Activity Dashboard" : "Open My Activities"} onClick={() => { if (user?.accountType === 'mentor') { navigate('/mentor-activity-dashboard'); } else { navigate('/activity-dashboard', { state: { view: 'magic-studio' } }); } }} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all">{user?.accountType === 'mentor' ? 'Mentor Activity Dashboard' : 'My Activities'}</button>
              </div>
            </div>
          </header>
          <div className="flex-1 min-h-0 overflow-auto max-w-7xl mx-auto w-full p-3 md:p-6">
        <p className="text-slate-700 text-center text-base md:text-lg mb-6">
          Select an art style to transform your artwork and learn about art history!
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
          {/* Left Panel - Original Artwork */}
          <div className="original-artwork">
            <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">
              Your Original Artwork
            </h3>
            {artwork && artworkUrlForDisplay ? (
              <div className="artwork-container">
                <div className="relative w-full max-w-full md:w-[500px] md:h-[500px] aspect-square rounded-2xl border-2 md:border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm mx-auto">
                  <img 
                    src={getFullImageUrl(artworkUrlForDisplay)} 
                    alt="Original artwork" 
                    className="w-full h-full object-contain bg-white"
                    onLoad={(e)=>{ try { console.log('✅ Original artwork loaded successfully:', e.currentTarget.src); } catch (_) {} }}
                    onError={(e)=>{ 
                      console.error('❌ Original artwork failed to load:', e.currentTarget.src);
                      console.error('❌ artworkUrlForDisplay:', artworkUrlForDisplay);
                      try {
                        const current = e.currentTarget.getAttribute('data-src') || e.currentTarget.src || artworkUrlForDisplay || '';
                        if (!current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                          console.log('🔄 Retrying via proxy:', current);
                          e.currentTarget.src = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                        }
                      } catch (_) {}
                    }}
                    data-src={artworkUrlForDisplay}
                  />
                  <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">
                    Your Artwork
                  </div>
                </div>
              </div>
            ) : (
              <div className="artwork-container">
                <div className="relative w-full max-w-full md:w-[500px] md:h-[500px] aspect-square rounded-2xl border-2 md:border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm flex items-center justify-center mx-auto">
                  <div className="text-center text-gray-500 px-4">
                    <p className="text-base md:text-lg font-semibold mb-2">No artwork selected</p>
                    <p className="text-xs md:text-sm">Please select an artwork from Mentor's Corner</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Style Selection */}
          <div className="style-selection">
            <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">
              {continueJourney ? '🚀 Continue Your Transformation Journey' : 'Choose Art Style'}
            </h3>
            
            {continueJourney && (
              <div className="mb-4 p-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg border border-purple-300">
                <p className="text-slate-700 text-sm">
                  ✨ You're continuing your artwork transformation journey! 
                  Each new transformation will be added to your existing trail.
                </p>
              </div>
            )}

            {/* Pending Unlock Slots Badge - Prominently displayed when user has pending slots */}
            {pendingUnlockSlots > 0 && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-400 to-orange-400 rounded-xl shadow-lg border-2 border-amber-300">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🎁</span>
                    <div>
                      <p className="text-white font-bold text-sm md:text-base">
                        You have {pendingUnlockSlots} unlock slot{pendingUnlockSlots > 1 ? 's' : ''} waiting!
                      </p>
                      <p className="text-amber-100 text-xs md:text-sm">
                        Claim them now to unlock more art styles for this artwork.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPendingUnlockModal(true)}
                    className="px-4 py-2 rounded-full bg-white text-amber-700 font-semibold shadow-md hover:bg-amber-50 transition-all whitespace-nowrap text-xs md:text-sm"
                  >
                    Claim Now →
                  </button>
                </div>
              </div>
            )}
            
            {/* Search Bar */}
            <div className="search-bar mb-6">
              <input 
                type="text"
                placeholder="Search art styles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none bg-white/90 backdrop-blur-sm text-amber-800 placeholder-amber-600"
              />
            </div>

            {/* Style Grid */}
            <div className="style-grid grid grid-cols-2 gap-6 max-h-[600px] overflow-y-auto">
              {filteredStyles.map(style => {
                const isUnlocked = !unlockedStyleIds || unlockedStyleIds.includes(style.id);
                const isSelected = selectedStyle?.id === style.id;
                return (
                  <button 
                    key={style.id}
                    className={`style-button relative w-full p-6 rounded-2xl border-3 transition-all duration-500 ${
                      isUnlocked
                        ? 'hover:scale-105 hover:shadow-xl'
                        : 'opacity-60 cursor-not-allowed'
                    } ${
                      isSelected && isUnlocked
                        ? 'bg-gradient-to-br from-amber-500 via-orange-400 to-yellow-400 border-amber-300 text-white shadow-2xl transform scale-105' 
                        : 'bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-50 border-amber-200 hover:border-amber-300 hover:from-amber-200 hover:via-orange-100 hover:to-yellow-100 text-amber-800 hover:shadow-lg'
                    }`}
                    onClick={() => {
                      if (!isUnlocked) return;
                      handleStyleSelect(style);
                    }}
                  >
                    <div className={`text-center ${!isUnlocked ? 'blur-[1px]' : ''}`}>
                      <div className="bg-gradient-to-r from-amber-400 to-orange-400 rounded-lg p-3 mb-3 shadow-md">
                        <h3 className="font-extrabold text-xl text-white text-shadow-sm">
                          {style.name}
                        </h3>
                      </div>
                      <p className="text-sm font-medium leading-relaxed opacity-95">
                        {style.briefDescription || style.kidFriendlyDescription?.split('!')[0] + '!' || "Transform your artwork with this unique style"}
                      </p>
                    </div>
                    {!isUnlocked && (
                      <div className="absolute inset-0 rounded-2xl bg-black/15 flex items-center justify-center pointer-events-none">
                        <span className="px-3 py-1 rounded-full bg-white/90 text-amber-800 text-xs font-semibold border border-amber-300">
                          Locked – upload via tutorial to unlock
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Confirmation Popup */}
            {showConfirmation && selectedStyle && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 max-w-md mx-4 shadow-2xl">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-amber-800 mb-4">
                      Transform Your Artwork?
                    </h3>
                    <p className="text-gray-700 mb-6">
                      Do you want to transform your artwork in <span className="font-semibold text-amber-600">"{selectedStyle.name}"</span> style?
                    </p>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setShowConfirmation(false)}
                        className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300"
                      >
                        No
                      </button>
                      <button
                        onClick={() => {
                          setShowConfirmation(false);
                          handleTransformArtwork();
                        }}
                        disabled={isProcessing}
                        className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Yes'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Processing overlay — matches Magic Studio / Art Appreciation loader (solid, legible) */}
        {isProcessing && (
          <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-stone-900/40 backdrop-blur-xl">
            <div className="bg-white rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 max-w-md w-full border-4 border-amber-300">
              <svg
                className="animate-spin h-12 w-12 text-amber-500 mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <div className="text-amber-800 font-bold text-lg text-center mb-3">
                Crafting your {selectedStyle?.name || 'new'} transformation
              </div>
              <div className="text-stone-600 text-xs text-center">
                Please keep this window open while we finish.
              </div>
            </div>
          </div>
        )}



        {/* Results Modal */}
        {showResults && transformedArtwork && (
          <div className="results-modal fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            {console.log('Rendering results modal with:', { showResults, transformedArtwork, selectedStyle })}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-amber-800">
                  Your Artwork in {selectedStyle.name} Style
                </h3>
                <button 
                  onClick={() => setShowResults(false)}
                  className="text-amber-600 hover:text-amber-800 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              
              {/* Results Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Original */}
                <div className="original-result">
                  <h4 className="font-semibold text-amber-800 mb-3 text-center">Original Artwork</h4>
                  <div className="relative w-full h-[400px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm">
                    <img 
                      src={artworkUrlForDisplay ? getFullImageUrl(artworkUrlForDisplay) : ''} 
                      alt="Original artwork" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">
                      Original
                    </div>
                  </div>
                </div>
                
                {/* Transformed */}
                <div className="transformed-result">
                  <h4 className="font-semibold text-amber-800 mb-3 text-center">{selectedStyle.name} Style</h4>
                  <div className="relative w-full h-[400px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm cursor-pointer hover:scale-105 transition-transform duration-300 group"
                    onClick={() => setShowExpandedView(true)}
                    onDoubleClick={() => setShowExpandedView(true)}
                  >
                    <img 
                      src={getFullImageUrl(transformedArtwork)} 
                      alt={`Artwork in ${selectedStyle.name} style`} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">
                      {selectedStyle.name}
                    </div>
                    <div className="absolute top-2 right-2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Double-click to expand!
                    </div>
                    <div className="absolute inset-0 bg-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </div>
                  <p className="text-sm text-amber-600 mt-2 text-center">
                    Double-click to learn more about {selectedStyle.name}!
                  </p>
                  
                  {/* Learn About button directly under transformed artwork */}
                  <div className="mt-4 text-center">
                    <button 
                      onClick={() => setShowExpandedView(true)}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                      style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                    >
                      {`Learn About ${selectedStyle.name}`}
                    </button>
                  </div>
                </div>
              </div>

              {/* Bottom Actions: Try Another + Learn How To Make - side by side */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setShowResults(false)}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                >
                  Try Another Style
                </button>
                <button 
                  onClick={handleStartTutorial}
                  className="bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 animate-pulse"
                  style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                >
                  {`Let's learn how to make this ${selectedStyle.name} artwork`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded View Modal — z above results modal so clicks / paint order stay correct */}
        {showExpandedView && selectedStyle && (
          <div
            className="expanded-modal fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-3 sm:p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 lg:p-8 max-w-[min(96vw,1200px)] w-full max-h-[95vh] overflow-y-auto overflow-x-hidden min-w-0 shadow-2xl border border-amber-100/80"
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <div className="text-center flex-1">
                  <h3 className="text-lg md:text-2xl lg:text-3xl font-bold text-amber-800 mb-1 md:mb-2" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>
                    Let's learn about {selectedStyle.name}
                  </h3>
                </div>
                <button 
                  onClick={() => {
                    setShowExpandedView(false);
                  }}
                  className="hidden md:block text-amber-600 hover:text-amber-800 text-2xl font-bold ml-4"
                >
                  ×
                </button>
              </div>
              
              {/* Expanded Content Grid — right column top aligns with transformed image */}
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,40%)_minmax(0,60%)] gap-6 lg:gap-8 lg:items-start w-full max-w-full min-w-0">
                <h4 className="text-xl font-semibold text-amber-800 mb-4 lg:mb-0 lg:col-start-1 lg:row-start-1 min-w-0 max-w-full">
                  Your Inspiration
                </h4>
                <div className="relative w-full h-[500px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm lg:col-start-1 lg:row-start-2 min-w-0 max-w-full">
                  <img 
                    src={getFullImageUrl(transformedArtwork)} 
                    alt={`Artwork in ${selectedStyle.name} style`} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-3 left-3 text-center text-amber-700 font-semibold bg-white/90 px-3 py-2 rounded-lg shadow">
                    {selectedStyle.name} Style
                  </div>
                  <button className="absolute top-3 right-3 bg-white/80 hover:bg-white text-amber-700 p-2 rounded-full shadow-lg transition-all duration-300" onClick={() => setShowArtworkFullscreen(true)} title="Expand">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4 flex justify-center px-1 lg:col-start-1 lg:row-start-3 lg:mt-4 min-w-0 max-w-full">
                  <button
                    type="button"
                    onClick={() => { handleStartTutorial(); setTimeout(() => setShowExpandedView(false), 10); }}
                    className="inline-flex items-center justify-center max-w-full bg-gradient-to-r from-amber-700 via-amber-600 to-orange-600 hover:from-amber-800 hover:via-amber-700 hover:to-orange-700 text-white px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium tracking-wide shadow-sm hover:shadow transition-all duration-200 border border-amber-800/20"
                    style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
                  >
                    <span className="text-center leading-snug">
                      {`Let's learn how to make this ${selectedStyle.name} artwork`}
                    </span>
                  </button>
                </div>
                {/* Right: Art Form Information — min-w-0 prevents grid overflow clipping */}
                <div className="info-section space-y-6 min-w-0 max-w-full w-full box-border pr-0 sm:pr-1 lg:col-start-2 lg:row-start-2 lg:row-span-2 lg:self-stretch lg:min-h-0">
                  {/* Shared artform schema: same Firestore facts as Learn Art History, expandable sections */}
                  {waysOfSeeingAccordionMeta && (
                    <div className="description-section min-w-0 max-w-full w-full box-border">
                      <ArtformDetailAccordions
                        artformKey={selectedStyle.id}
                        styleMeta={waysOfSeeingAccordionMeta}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <button 
                  onClick={() => setShowExpandedView(false)}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                >
                  Back to Results
                </button>
                <button 
                  onClick={() => {
                    setShowExpandedView(false);
                    setShowResults(false);
                  }}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                >
                  Try Another Style
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Image Viewer */}
        {showArtworkFullscreen && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000] p-4">
            <button
              onClick={() => setShowArtworkFullscreen(false)}
              className="absolute top-6 right-6 bg-white/90 rounded-full px-3 py-2 text-amber-700 font-bold shadow hover:bg-white"
            >
              ×
            </button>
            <img
              src={getFullImageUrl(transformedArtwork)}
              alt="Expanded artwork"
              className="max-w-[95vw] max-h-[90vh] rounded-2xl border-4 border-amber-200 shadow-2xl bg-white"
            />
          </div>
        )}

        {/* Step-by-Step Learning Modal */}
        {showStepByStepLearning && selectedStyle && (
          <StepByStepLearning
            transformedImage={transformedArtwork ? getFullImageUrl(transformedArtwork) : null}
            originalImageUrl={artworkUrlForDisplay ? getFullImageUrl(artworkUrlForDisplay) : ''}
            styleInfo={selectedStyle}
            onClose={() => setShowStepByStepLearning(false)}
            onBackToResults={() => {
              setShowStepByStepLearning(false);
              setShowResults(true); // Show the results modal (first page after tutorial generation with "Things to Remember")
            }}
            userAge={userAge}
            userId={user?.uid}
            artworkId={currentJourneyId || ''}
            reimagineActivityId={currentJourneyId || null}
            forceGenerate={forceGenerate} // Pass the forceGenerate flag
          />
        )}

        {/* Choose Your First 3 Styles Modal */}
        {showFirst3Modal && unlockedStyleIds && unlockedStyleIds.length === 0 && (
          <UnlockStylesModal
            isOpen={showFirst3Modal}
            onClose={() => setShowFirst3Modal(false)}
            slotsToUnlock={3}
            availableStyles={artStyles}
            artworkUrl={artworkUrlForDisplay}
            userId={user?.uid}
            title="Choose Your First 3 Art Styles!"
            description="Welcome to Ways of Seeing! Choose 3 art styles you'd like to explore first. You can unlock more styles by uploading your artwork via tutorial."
            onUnlockComplete={async (selectedIds) => {
              if (selectedIds.length > 0 && artworkUrlForDisplay && user?.uid) {
                await addUnlockedArtStyles(artworkUrlForDisplay, user.uid, selectedIds);
                const state = await getOrCreateArtStyleUnlockState(artworkUrlForDisplay, user.uid);
                setUnlockedStyleIds(state.unlockedStyleIds || []);
                setPendingUnlockSlots(state.pendingUnlockSlots || 0);
                setShowFirst3Modal(false);
              }
            }}
          />
        )}

        {/* Claim Pending Unlock Slots Modal */}
        {showPendingUnlockModal && pendingUnlockSlots > 0 && (
          <UnlockStylesModal
            isOpen={showPendingUnlockModal}
            onClose={() => setShowPendingUnlockModal(false)}
            slotsToUnlock={pendingUnlockSlots}
            availableStyles={artStyles.filter(s => !unlockedStyleIds?.includes(s.id))}
            artworkUrl={artworkUrlForDisplay}
            userId={user?.uid}
            title="Claim Your Unlock Slots!"
            description={`You have ${pendingUnlockSlots} unlock slot${pendingUnlockSlots > 1 ? 's' : ''} waiting! Choose which art style${pendingUnlockSlots > 1 ? 's' : ''} you want to unlock.`}
            onUnlockComplete={async (selectedIds) => {
              if (selectedIds.length > 0 && artworkUrlForDisplay && user?.uid) {
                await addUnlockedArtStyles(artworkUrlForDisplay, user.uid, selectedIds);
                const state = await getOrCreateArtStyleUnlockState(artworkUrlForDisplay, user.uid);
                setUnlockedStyleIds(state.unlockedStyleIds || []);
                setPendingUnlockSlots(state.pendingUnlockSlots || 0);
                setShowPendingUnlockModal(false);
              }
            }}
          />
        )}
        <UpgradeQuotaModal
          isOpen={showUpgradeModal}
          onClose={dismissUpgradeModal}
          feature={upgradeModalFeature}
          onUpgrade={() => { dismissUpgradeModal(); navigate('/membership'); }}
        />
        </div>
        </div>
      </div>
    </div>
  );
};

export default ArtStyleSelectionPage; 